import cx from 'classnames';
import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import plusSvg from 'assets/icons/plus.svg';
import Button, { Align, Color, Size } from 'lib/Button';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import { Store } from 'components/common/types/Store.types';
import { ProductLine, SelectionPermission } from 'store/productManagement/reducer.types';
import {
  addProductLine,
  changeActiveProductLineId,
  syncActiveLineFromNewProduct,
  syncNewProductFromActiveLine,
} from 'store/productManagement/reducer';
import useHeaderLinesLayout from 'components/pages/Planner/hooks/useHeaderLinesLayout';
import { hasProductRequiredFields } from 'utils/hasProductRequiredFields';
import { getIsReadOnly, getNewProduct } from 'store/productManagement/selectors';

const ProductLineTabs: React.FC = () => {
  const dispatch = useDispatch();
  const productLines = useSelector((state: Store) => state.productManagement.productLines);
  const activeLineId = useSelector((state: Store) => state.productManagement.activeLineId);
  const { mediaType, channels, pricingMode, range, budget, frames, impressions, networks } = useSelector(getNewProduct);
  const isReadOnly = useSelector(getIsReadOnly);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [displayedLines, setDisplayedLines] = useState<ProductLine[]>(productLines);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const areNetworksEmpty = isEmpty(networks) || networks?.selectionPermission === SelectionPermission.DISABLED;
  const hasRequiredFields = hasProductRequiredFields({
    mediaType,
    channels,
    pricingMode,
    range,
    budget,
    impressions,
    frames,
    areNetworksEmpty,
  });

  const isAddLineDisabled = isReadOnly || !hasRequiredFields;

  useHeaderLinesLayout({
    headerContainerRef: containerRef,
    lines: productLines,
    currentLineId: activeLineId,
    setDisplayedLines,
    setDisplayDealLinesDropdown: setShowDropdown,
  });

  const handleLineSelect = (line: ProductLine): void => {
    if (line.lineId !== activeLineId) {
      dispatch(syncActiveLineFromNewProduct());
      dispatch(changeActiveProductLineId(line.lineId));
      dispatch(syncNewProductFromActiveLine());
    }
    setIsDropdownOpen(false);
  };

  const handleAddNewLine = (): void => {
    dispatch(syncActiveLineFromNewProduct());
    dispatch(addProductLine());
    dispatch(syncNewProductFromActiveLine());
    setIsDropdownOpen(false);
  };

  const getAddLineTooltip = (): string => {
    if (isReadOnly) {
      return 'Cannot edit in read-only mode';
    }
    if (hasRequiredFields) {
      return 'Add new line';
    }
    return 'Complete required fields before adding a new line';
  };

  return (
    <div ref={containerRef} data-test-id="product-line-tabs" className="flex items-center space-x-2 mb-2 w-full">
      {displayedLines.map((line) => {
        const isActive = line.lineId === activeLineId;

        return (
          <Tooltip
            tooltip={line.name}
            direction={TooltipDirection.BOTTOM}
            contentSize={TooltipSize.MAX}
            key={line.lineId}
          >
            <Button
              onClick={() => handleLineSelect(line)}
              color={isActive ? Color.TERTIARY : Color.BLACK_AND_WHITE}
              dataTestId={`product-line-tab-${line.lineId}`}
              size={Size.SMALL}
              label={line.name}
              align={Align.START}
              classNames={cx('border flex flex-row-reverse max-w-[15ch] overflow-hidden leading-tight', {
                'border-primary-600': isActive,
              })}
            />
          </Tooltip>
        );
      })}

      {showDropdown ? (
        <div data-test-id="product-line-dropdown">
          <Popover
            containerClassName="z-30 py-1"
            align="end"
            positions={['bottom']}
            isOpen={isDropdownOpen}
            onClickOutside={() => setIsDropdownOpen(false)}
            content={({ position, childRect, popoverRect }) => (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor="var(--transparent-color)"
                arrowSize={0}
              >
                <div className="relative w-80 bg-neutral-50 shadow-md border border-neutral-300 rounded-md">
                  <div className="flex flex-col">
                    <div className="w-full flex justify-start items-center space-x-2 py-4 px-4 border-b border-neutral-300">
                      <div
                        data-test-id="product-line-dropdown-header"
                        className="w-full flex justify-between items-center body-base space-x-2 mx-1"
                      >
                        <p className="sub-header-base font-bold">Product Lines</p>
                        <Button
                          classNames="text-primary shadow-inner-sm border border-neutral-300"
                          dataTestId="product-line-dropdown-new-line-button"
                          label="New Line"
                          svg={plusSvg}
                          color={Color.TRANSPARENT}
                          onClick={handleAddNewLine}
                          isDisabled={isAddLineDisabled}
                        />
                      </div>
                    </div>
                    <div
                      data-test-id="product-line-dropdown-list"
                      className="max-h-[500px] overflow-y-auto py-1 px-2.5"
                    >
                      {productLines.map((line) => {
                        const isActive = line.lineId === activeLineId;

                        return (
                          <div
                            data-test-id={`product-line-option-${line.lineId}`}
                            key={`option-${line.lineId}`}
                            className={cx('w-full flex justify-start items-center space-x-2 px-2.5 rounded-md', {
                              'bg-neutral-950-opacity-5 sub-header-base text-primary': isActive,
                              'hover:bg-primary-600-opacity-5 hover:text-primary': !isActive,
                            })}
                          >
                            <button
                              className="py-2 w-full flex justify-between items-center body-base space-x-2"
                              type="button"
                              onClick={() => handleLineSelect(line)}
                            >
                              <p className="w-10/12 text-left body-base truncate whitespace-nowrap overflow-hidden">
                                {line.name}
                              </p>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </ArrowContainer>
            )}
          >
            <button
              className={cx(
                'bg-neutral-950-opacity-5 sub-header-base text-primary w-[30px] h-[24px] border rounded-md hover:bg-neutral-950-opacity-10',
                {
                  'border-neutral-400': isDropdownOpen,
                  'border-transparent': !isDropdownOpen,
                },
              )}
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              data-test-id="product-line-dropdown-trigger"
              aria-label="Show all lines"
            >
              <p>...</p>
            </button>
          </Popover>
        </div>
      ) : (
        <Tooltip tooltip={getAddLineTooltip()} direction={TooltipDirection.BOTTOM} contentSize={TooltipSize.MAX}>
          <Button
            classNames="flex flex-row-reverse leading-tight border border-transparent text-primary-600"
            onClick={handleAddNewLine}
            color={Color.TRANSPARENT}
            size={Size.SMALL}
            label="+ New line"
            dataTestId="add-product-line-button"
            isDisabled={isAddLineDisabled}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default ProductLineTabs;
